import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Table, Form, Button, Space, Tag, Switch, Card } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

import { setAppTitle } from 'src/store/app';
import {
  commandsListSelector,
  commandsMetaDataSelector,
  fetchCommands,
  isLoadingFetchCommandsSelector,
  updateCommand,
  deleteCommand
} from 'src/store/command';

import { CommandsModal } from './Modal/Modal';

import './commands.scss';

export const Commands = () => {
  const { t } = useTranslation('commands');
  const dispatch = useDispatch();

  const data = useSelector(commandsListSelector);
  const commandsMeta = useSelector(commandsMetaDataSelector);
  const isLoading = useSelector(isLoadingFetchCommandsSelector);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCommand, setSelectedCommand] = useState(null);
  const [commandsPagination, setCommandsPagination] = useState({ page: 1, perPage: 10 });

  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(setAppTitle(t('menu:commands')));
  }, [dispatch, t]);

  useEffect(() => {
    dispatch(fetchCommands(commandsPagination));
  }, [dispatch, commandsPagination]);

  const handleCommandsPageChange = useCallback((pagination) => {
    setCommandsPagination({
      page: pagination?.current || 1,
      perPage: pagination?.pageSize || 10
    });
  }, []);

  const openCreateModal = useCallback(() => {
    setSelectedCommand(null);
    form.resetFields();
    form.setFieldsValue({
      type: 'default',
      conditions: [],
      ifMessage: '',
      claimedMessage: '',
      elseMessage: '',
      trackLurkers: true,
      isFollower: true,
      isGiveawayCommand: false,
      maxTickets: undefined,
      active: true
    });
    setIsModalOpen(true);
  }, [form]);

  const openEditModal = useCallback(
    (item) => () => {
      setSelectedCommand(item);
      form.resetFields();
      form.setFieldsValue({
        ...item,
        conditions: Array.isArray(item?.conditions) ? item.conditions : [],
        claimedMessage: item?.claimedMessage ?? ''
      });
      setIsModalOpen(true);
    },
    [form]
  );

  const onToggleActive = useCallback(
    (item) => (checked) => {
      dispatch(updateCommand({ id: item?._id, payload: { ...item, active: checked } }));
    },
    [dispatch]
  );

  const onDelete = useCallback(
    (item) => () => {
      dispatch(deleteCommand({ id: item?._id }));
    },
    [dispatch]
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedCommand(null);
    form.resetFields();
  }, [form]);

  const columns = [
    {
      title: t('table.type'),
      dataIndex: 'type',
      key: 'type',
      render: (type) => <Tag color="purple">{type}</Tag>
    },
    {
      title: t('table.trigger'),
      dataIndex: 'trigger',
      key: 'trigger'
    },
    {
      title: t('table.active'),
      dataIndex: 'active',
      key: 'active',
      width: 150,
      render: (active, item) => <Switch checked={active} onChange={onToggleActive(item)} />
    },
    {
      title: t('table.actions'),
      key: 'actions',
      width: 150,
      render: (_, item) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={openEditModal(item)}>
            {t('actions.edit')}
          </Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={onDelete(item)}>
            {t('actions.delete')}
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div className="commands">
      <div className="commands__content">
        <div className="commands__header">
          <div />
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            {t('actions.create_command')}
          </Button>
        </div>

        <Card className="commands__card" bordered={false}>
          <Table
            rowKey={(row) => row?._id || row?.id}
            columns={columns}
            dataSource={data}
            onChange={handleCommandsPageChange}
            pagination={{
              current: commandsMeta?.page || commandsPagination.page,
              pageSize: commandsMeta?.perPage || commandsPagination.perPage,
              total: commandsMeta?.total || 0,
              showSizeChanger: true
            }}
            loading={isLoading}
          />
        </Card>
      </div>

      <CommandsModal
        form={form}
        selectedCommand={selectedCommand}
        isModalOpen={isModalOpen}
        onCancel={closeModal}
      />
    </div>
  );
};
